import React from 'react';

/**
 * Registry of Gift-with-Purchase (GwP) partner landing pages.
 *
 * Each entry maps a lowercased URL slug (the partner code prefix, e.g. "table26")
 * to a lazy-loaded React component that renders the partner's landing page.
 *
 * Routing flow:
 * 1. A visitor lands on `/products?pc=<prefix>-<suffix>`.
 * 2. `ProductListingPage` extracts the prefix (portion before the first `-`) and
 *    checks the registry. If a partner page is registered, it redirects to
 *    `/partners/<prefix>?pc=<full-code>`.
 * 3. `/partners/:partnerSlug` (see `PartnerLandingPage`) renders the registered
 *    component. If the slug is not registered, it falls back to `/products`
 *    while preserving the `pc` query parameter.
 *
 * To add a new partner:
 * - Add a component file under `partners/<PartnerName>.tsx`.
 * - Register it here under its lowercased URL slug.
 */
export const PARTNERS_REGISTRY: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  table26: React.lazy(() => import('./partners/Table26')),
  ollie26: React.lazy(() => import('./partners/Ollie')),
  st26: React.lazy(() => import('./partners/SpotAndTango')),
  impact26: React.lazy(() => import('./partners/Impact')),
  'bark-2026': React.lazy(() => import('./partners/Bark')),
  zoom26: React.lazy(() => import('./partners/Zoom')),
  healthy: React.lazy(() => import('./partners/Healthy')),
  cc26: React.lazy(() => import('./partners/CreatureComforts')),
  sundays26: React.lazy(() => import('./partners/Sundays')),
  metlife99: React.lazy(() => import('./partners/Metlife')),
  fetch99: React.lazy(() => import('./partners/Fetch')),
  woofies99: React.lazy(() => import('./partners/Woofies')),
  sniff99: React.lazy(() => import('./partners/SniffNSnack')),
  impact99: React.lazy(() => import('./partners/ImpactCrates')),
  bark99: React.lazy(() => import('./partners/BarkBox')),
  king99: React.lazy(() => import('./partners/KingLou')),
  wagmo99: React.lazy(() => import('./partners/Wagmo')),
  vetster99: React.lazy(() => import('./partners/Vetster')),
  perkspot99: React.lazy(() => import('./partners/PerkSpot')),
  dogo99: React.lazy(() => import('./partners/Dogo')),
  petstable99: React.lazy(() => import('./partners/PetsTable')),
  pupsocial99: React.lazy(() => import('./partners/PupSocial')),
  nutricanine99: React.lazy(() => import('./partners/NutriCanine')),
};

/**
 * Returns the list of all registered partner slugs.
 */
export function getRegisteredPartnerSlugs(): string[] {
  return Object.keys(PARTNERS_REGISTRY);
}

/**
 * Extracts the partner prefix from a GwP coupon code.
 *
 * The prefix is the portion of the code before the first hyphen, lowercased.
 * For example:
 *   - "table26-70h0j" -> "table26"
 *   - "BARK-FI-562zrq" -> "bark"
 *   - "standalone"     -> "standalone"
 *   - "" / undefined   -> null
 */
export function extractPartnerPrefix(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  const trimmed = code.trim();
  if (!trimmed) {
    return null;
  }
  const firstHyphenIndex = trimmed.indexOf('-');
  const prefix = firstHyphenIndex === -1 ? trimmed : trimmed.slice(0, firstHyphenIndex);
  return prefix.toLowerCase();
}

/**
 * Own-property check to avoid false positives from prototype-chain keys
 * (`__proto__`, `constructor`, `toString`, ...) when evaluating user-
 * controlled input. Using `key in obj` or `obj[key]` directly against a
 * plain-object registry can resolve to `Object.prototype` members, which
 * would lead either to incorrect redirects or to runtime crashes when a
 * prototype value is later rendered as a React component.
 */
function isRegisteredPartnerSlug(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(PARTNERS_REGISTRY, slug);
}

/**
 * Returns the registered partner slug for a given coupon code, or null if
 * no partner is registered for that code's prefix.
 *
 * Performs a longest-match lookup against the registry so that multi-segment
 * slugs work alongside single-segment ones. For example, with a registry
 * containing both `bark` and `bark-2026`:
 *   - "bark-2026-xyz" -> "bark-2026" (longer match wins)
 *   - "bark-fi-xyz"   -> "bark" (only the single-segment prefix matches)
 */
export function getPartnerSlugForCode(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  const trimmed = code.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  const segments = trimmed.split('-');
  // Walk from the longest possible prefix down to the first segment so we
  // pick the most specific registered slug. Stop at length-1 because a code
  // with no suffix (e.g. just "table26") still has a coupon shape mismatch
  // but we keep the 1-segment case to preserve historical behaviour.
  for (let i = segments.length; i > 0; i--) {
    const candidate = segments.slice(0, i).join('-');
    if (isRegisteredPartnerSlug(candidate)) {
      return candidate;
    }
  }
  return null;
}

/**
 * Returns the registered partner component for a given slug, or null if
 * no partner is registered under that slug.
 *
 * The slug lookup is case-insensitive so that URLs like `/partners/Table26`
 * and `/partners/table26` both resolve to the same partner.
 */
export function getPartnerComponentForSlug(
  slug: string | null | undefined,
): React.LazyExoticComponent<React.ComponentType> | null {
  if (!slug) {
    return null;
  }
  const normalized = slug.trim().toLowerCase();
  return isRegisteredPartnerSlug(normalized) ? PARTNERS_REGISTRY[normalized] : null;
}
